export type GameFeedbackCue =
  | "capture"
  | "cut"
  | "death"
  | "respawn"
  | "unlock"
  | "team_win"
  | "team_loss"
  | "reconnect_success"
  | "reconnect_failure";

export interface GameFeedbackPreferences {
  soundEnabled: boolean;
  hapticsEnabled: boolean;
}

interface FeedbackNote {
  frequencyHz: number;
  endFrequencyHz?: number;
  durationMs: number;
  delayMs: number;
  gain: number;
  wave: OscillatorType;
}

interface FeedbackCueDefinition {
  notes: readonly FeedbackNote[];
  vibration: number | readonly number[];
}

/**
 * Single tuning surface for Yak Trails feedback. The sounds are deliberately
 * short synthesized cues: they add tactile game feel without downloading or
 * decoding audio assets on mobile.
 */
export const YAK_TRAILS_FEEDBACK_CONFIG = {
  preferenceVersion: 1,
  storageKey: "yak-trails:feedback-preferences",
  duplicateHistoryLimit: 512,
  masterGain: 0.16,
  attackMs: 6,
  releaseFloor: 0.0001,
  cues: {
    capture: {
      notes: [
        { frequencyHz: 620, endFrequencyHz: 820, durationMs: 90, delayMs: 0, gain: 0.72, wave: "sine" },
        { frequencyHz: 880, endFrequencyHz: 1_080, durationMs: 105, delayMs: 52, gain: 0.5, wave: "sine" },
      ],
      vibration: 18,
    },
    cut: {
      notes: [
        { frequencyHz: 1_020, endFrequencyHz: 520, durationMs: 105, delayMs: 0, gain: 0.82, wave: "square" },
        { frequencyHz: 280, endFrequencyHz: 180, durationMs: 125, delayMs: 34, gain: 0.42, wave: "triangle" },
      ],
      vibration: [26, 18, 34],
    },
    death: {
      notes: [
        { frequencyHz: 300, endFrequencyHz: 110, durationMs: 230, delayMs: 0, gain: 0.78, wave: "sawtooth" },
        { frequencyHz: 180, endFrequencyHz: 82, durationMs: 260, delayMs: 54, gain: 0.46, wave: "triangle" },
      ],
      vibration: [45, 25, 70],
    },
    respawn: {
      notes: [
        { frequencyHz: 360, endFrequencyHz: 520, durationMs: 90, delayMs: 0, gain: 0.5, wave: "sine" },
        { frequencyHz: 560, endFrequencyHz: 760, durationMs: 115, delayMs: 76, gain: 0.68, wave: "sine" },
      ],
      vibration: 24,
    },
    unlock: {
      notes: [
        { frequencyHz: 523, durationMs: 135, delayMs: 0, gain: 0.5, wave: "sine" },
        { frequencyHz: 659, durationMs: 145, delayMs: 92, gain: 0.58, wave: "sine" },
        { frequencyHz: 784, durationMs: 190, delayMs: 184, gain: 0.68, wave: "sine" },
      ],
      vibration: [22, 38, 22],
    },
    team_win: {
      notes: [
        { frequencyHz: 392, durationMs: 150, delayMs: 0, gain: 0.48, wave: "triangle" },
        { frequencyHz: 523, durationMs: 160, delayMs: 105, gain: 0.58, wave: "triangle" },
        { frequencyHz: 659, durationMs: 220, delayMs: 210, gain: 0.72, wave: "triangle" },
      ],
      vibration: [35, 35, 35, 35, 70],
    },
    team_loss: {
      notes: [
        { frequencyHz: 330, endFrequencyHz: 250, durationMs: 170, delayMs: 0, gain: 0.56, wave: "triangle" },
        { frequencyHz: 247, endFrequencyHz: 150, durationMs: 240, delayMs: 142, gain: 0.62, wave: "triangle" },
      ],
      vibration: [70, 35, 70],
    },
    reconnect_success: {
      notes: [
        { frequencyHz: 440, durationMs: 75, delayMs: 0, gain: 0.38, wave: "sine" },
        { frequencyHz: 660, durationMs: 115, delayMs: 62, gain: 0.48, wave: "sine" },
      ],
      vibration: 14,
    },
    reconnect_failure: {
      notes: [
        { frequencyHz: 220, endFrequencyHz: 170, durationMs: 135, delayMs: 0, gain: 0.5, wave: "triangle" },
        { frequencyHz: 180, endFrequencyHz: 125, durationMs: 180, delayMs: 145, gain: 0.56, wave: "triangle" },
      ],
      vibration: [50, 45, 50],
    },
  } satisfies Record<GameFeedbackCue, FeedbackCueDefinition>,
} as const;

interface FeedbackStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface FeedbackVisibilitySource {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

interface FeedbackAudioParam {
  setValueAtTime(value: number, startTime: number): unknown;
  exponentialRampToValueAtTime(value: number, endTime: number): unknown;
}

interface FeedbackAudioNode {
  connect(destination: unknown): unknown;
}

interface FeedbackOscillator extends FeedbackAudioNode {
  type: OscillatorType;
  readonly frequency: FeedbackAudioParam;
  start(when?: number): void;
  stop(when?: number): void;
}

interface FeedbackGain extends FeedbackAudioNode {
  readonly gain: FeedbackAudioParam;
}

export interface FeedbackAudioContext {
  readonly currentTime: number;
  readonly destination: unknown;
  readonly state: AudioContextState;
  createOscillator(): FeedbackOscillator;
  createGain(): FeedbackGain;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  close?(): Promise<void>;
}

type FeedbackAudioContextFactory = () => FeedbackAudioContext | null;
type FeedbackVibrate = (pattern: number | readonly number[]) => boolean;
type PreferenceListener = (preferences: Readonly<GameFeedbackPreferences>) => void;

export interface GameFeedbackControllerOptions {
  storage?: FeedbackStorage | null;
  visibility?: FeedbackVisibilitySource | null;
  createAudioContext?: FeedbackAudioContextFactory;
  vibrate?: FeedbackVibrate | null;
}

const DEFAULT_PREFERENCES: GameFeedbackPreferences = {
  soundEnabled: true,
  hapticsEnabled: true,
};

function browserStorage(): FeedbackStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function browserVisibility(): FeedbackVisibilitySource | null {
  return typeof document === "undefined"
    ? null
    : document as unknown as FeedbackVisibilitySource;
}

function browserAudioContext(): FeedbackAudioContext | null {
  if (typeof globalThis === "undefined") return null;
  const scope = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextConstructor = scope.AudioContext ?? scope.webkitAudioContext;
  return AudioContextConstructor
    ? new AudioContextConstructor() as unknown as FeedbackAudioContext
    : null;
}

function browserVibrate(pattern: number | readonly number[]): boolean {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return false;
  const browserPattern = typeof pattern === "number" ? pattern : [...pattern];
  return navigator.vibrate(browserPattern);
}

function loadPreferences(storage: FeedbackStorage | null): GameFeedbackPreferences {
  if (!storage) return { ...DEFAULT_PREFERENCES };
  try {
    const raw = storage.getItem(YAK_TRAILS_FEEDBACK_CONFIG.storageKey);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const saved = JSON.parse(raw) as Partial<GameFeedbackPreferences> & { version?: number };
    if (saved.version !== YAK_TRAILS_FEEDBACK_CONFIG.preferenceVersion) return { ...DEFAULT_PREFERENCES };
    return {
      soundEnabled: typeof saved.soundEnabled === "boolean" ? saved.soundEnabled : true,
      hapticsEnabled: typeof saved.hapticsEnabled === "boolean" ? saved.hapticsEnabled : true,
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

/**
 * Lifecycle-safe, React-agnostic game feedback. Create one controller for the
 * mounted game, call `unlock()` from a pointer/keyboard gesture, and feed it
 * stable authoritative IDs via `emit()`.
 */
export class GameFeedbackController {
  private readonly storage: FeedbackStorage | null;
  private readonly visibility: FeedbackVisibilitySource | null;
  private readonly createAudioContext: FeedbackAudioContextFactory;
  private readonly vibrate: FeedbackVibrate | null;
  private readonly listeners = new Set<PreferenceListener>();
  private readonly seenEvents = new Set<string>();
  private readonly eventOrder: string[] = [];
  private readonly onVisibilityChange = () => { void this.syncVisibility(); };
  private preferences: GameFeedbackPreferences;
  private audioContext: FeedbackAudioContext | null = null;
  private disposed = false;

  constructor(options: GameFeedbackControllerOptions = {}) {
    this.storage = options.storage === undefined ? browserStorage() : options.storage;
    this.visibility = options.visibility === undefined ? browserVisibility() : options.visibility;
    this.createAudioContext = options.createAudioContext ?? browserAudioContext;
    this.vibrate = options.vibrate === undefined ? browserVibrate : options.vibrate;
    this.preferences = loadPreferences(this.storage);
    this.visibility?.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  getPreferences(): Readonly<GameFeedbackPreferences> {
    return { ...this.preferences };
  }

  subscribe(listener: PreferenceListener): () => void {
    this.listeners.add(listener);
    listener(this.getPreferences());
    return () => this.listeners.delete(listener);
  }

  setSoundEnabled(soundEnabled: boolean): void {
    if (this.preferences.soundEnabled === soundEnabled) return;
    this.preferences = { ...this.preferences, soundEnabled };
    this.persistAndNotify();
    void this.syncVisibility();
  }

  setHapticsEnabled(hapticsEnabled: boolean): void {
    if (this.preferences.hapticsEnabled === hapticsEnabled) return;
    this.preferences = { ...this.preferences, hapticsEnabled };
    this.persistAndNotify();
  }

  /** Creates and resumes Web Audio only after a user gesture. */
  async unlock(): Promise<boolean> {
    if (this.disposed || !this.preferences.soundEnabled || this.visibility?.hidden) return false;
    this.audioContext ??= this.createAudioContext();
    if (!this.audioContext) return false;
    if (this.audioContext.state !== "running") await this.audioContext.resume();
    return this.audioContext.state === "running";
  }

  /**
   * Emits a cue once for a stable event ID. Duplicate suppression happens even
   * while muted/hidden so old room events cannot play after settings change.
   */
  emit(cue: GameFeedbackCue, eventId: string): boolean {
    if (this.disposed) return false;
    const dedupeKey = `${cue}:${eventId}`;
    if (this.seenEvents.has(dedupeKey)) return false;
    this.rememberEvent(dedupeKey);
    if (this.visibility?.hidden) return true;

    const definition = YAK_TRAILS_FEEDBACK_CONFIG.cues[cue];
    if (this.preferences.soundEnabled && this.audioContext?.state === "running") {
      this.playNotes(definition.notes);
    }
    if (this.preferences.hapticsEnabled && this.vibrate) {
      try {
        this.vibrate(definition.vibration);
      } catch {
        // Browser vibration support can change with page lifecycle or policy.
      }
    }
    return true;
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.visibility?.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.listeners.clear();
    const context = this.audioContext;
    this.audioContext = null;
    if (context?.close) await context.close();
    else if (context?.state === "running") await context.suspend();
  }

  private playNotes(notes: readonly FeedbackNote[]): void {
    const context = this.audioContext;
    if (!context) return;
    for (const note of notes) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startAt = context.currentTime + note.delayMs / 1_000;
      const endAt = startAt + note.durationMs / 1_000;
      const attackAt = Math.min(endAt, startAt + YAK_TRAILS_FEEDBACK_CONFIG.attackMs / 1_000);
      oscillator.type = note.wave;
      oscillator.frequency.setValueAtTime(note.frequencyHz, startAt);
      if (note.endFrequencyHz) {
        oscillator.frequency.exponentialRampToValueAtTime(note.endFrequencyHz, endAt);
      }
      gain.gain.setValueAtTime(YAK_TRAILS_FEEDBACK_CONFIG.releaseFloor, startAt);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(YAK_TRAILS_FEEDBACK_CONFIG.releaseFloor, note.gain * YAK_TRAILS_FEEDBACK_CONFIG.masterGain),
        attackAt,
      );
      gain.gain.exponentialRampToValueAtTime(YAK_TRAILS_FEEDBACK_CONFIG.releaseFloor, endAt);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startAt);
      oscillator.stop(endAt);
    }
  }

  private rememberEvent(eventId: string): void {
    this.seenEvents.add(eventId);
    this.eventOrder.push(eventId);
    while (this.eventOrder.length > YAK_TRAILS_FEEDBACK_CONFIG.duplicateHistoryLimit) {
      const oldest = this.eventOrder.shift();
      if (oldest) this.seenEvents.delete(oldest);
    }
  }

  private persistAndNotify(): void {
    try {
      this.storage?.setItem(YAK_TRAILS_FEEDBACK_CONFIG.storageKey, JSON.stringify({
        version: YAK_TRAILS_FEEDBACK_CONFIG.preferenceVersion,
        ...this.preferences,
      }));
    } catch {
      // Storage can be unavailable in private browsing; preferences still work in-memory.
    }
    const value = this.getPreferences();
    for (const listener of this.listeners) listener(value);
  }

  private async syncVisibility(): Promise<void> {
    const context = this.audioContext;
    if (!context || this.disposed) return;
    if (this.visibility?.hidden || !this.preferences.soundEnabled) {
      if (context.state === "running") await context.suspend();
    } else if (context.state !== "running") {
      await context.resume();
    }
  }
}
